import { Vector2d } from "konva/lib/types";
import { Customer } from "../models/customer";
import { User } from "../models/user";
import { DRAW_FACTOR, LARGHEZZA_MAX_SETTORE_A, LARGHEZZA_MAX_SETTORE_F } from "./common_constants";
import { AxiosRequestConfig } from "axios";
import { NullableString, Step, StepSlugsEnum } from "./common_types";
import { Vector } from "vecti";
import { GlasspanelPosition } from "../models/door_configuration";

export function toDrawUnit(value: number): number {
    return value * DRAW_FACTOR;
}

export function toMillimeters(value: number): number {
    return value / DRAW_FACTOR;
}

export function getCurrentUser(storeBase: any): User {
    return storeBase.user as User;
}

export function getCurrentCustomer(storeBase: any): Customer | null {
    return storeBase.customer ? storeBase.customer as Customer : null;
}

export function getAxiosConfig(currentUser: User): AxiosRequestConfig {
    return {
        headers: {
            Authorization: "Bearer " + currentUser.token,
        },
    };
}

export function Vector2dToPointsArray(points: Vector2d[]): number[] {
    let res: number[] = [];
    points.forEach(p => {
        res.push(p.x);
        res.push(p.y);
    });
    return res;
}

export function VectorToPointsArray(points: Vector[]): number[] {
    let res: number[] = [];
    points.forEach(p => {
        res.push(p.x);
        res.push(p.y);
    });
    return res;
}

export function distance(x1: number, y1: number, x2: number, y2: number) {
    let x = x1 - x2;
    let y = y1 - y2;

    return Math.sqrt(x*x + y*y);
}

export function radiants(x1: number, y1: number, x2: number, y2: number) {
    return Math.atan2(y2-y1, x2-x1);
}

export function degree(x1: number, y1: number, x2: number, y2: number) {
    return radiants(x1, y1, x2, y2) * 180 / Math.PI;
}

export function midpoint(x1: number, y1: number, x2: number, y2: number): {x: number, y: number} {
    return {x: (x1 + x2) / 2, y: (y1 + y2) / 2};
}

export function numberCompare(a: number, b: number): number {
    if (a > b) return -1;
    if (a < b) return 1;
    return 0;
}

export function error(error: any) {
    if (error.response.data && !error.response.data.success && error.response.data.message) {
        throw error.response.data.message;
    } else {
        throw error.response.statusText;
    }
}

export function isStepVisible(stepSlug: StepSlugsEnum, steps: Step[]): boolean {
    let step = steps.find(s => s.slug == stepSlug);
    return step?.isVisible ?? false;
}
/**
 * La vetrata integrale della gamma PLANA si presenta in piu' forme a seconda di quando e'
 * stata caricata a catalogo: come FINITURA "GLASSPANEL" (versione storica), come MODELLO
 * "PLANA FULLGLASS" (opzione 75, tavola "OVERLAP FULL GLASS") e come MODELLO "GLASSPANEL"
 * creato dal seeder con finiture proprie (vetro 6/7, vetro camera 4/12/4, policarbonato
 * Makrolon UV 6mm). Il disegno 2D/3D e' lo stesso: le riconosciamo tutte.
 */
export function isGlasspanelSelection(modelloName: NullableString, finituraName: NullableString): boolean {
    return isGlasspanelName(modelloName) || isGlasspanelName(finituraName);
}

function isGlasspanelName(name: NullableString): boolean {
    return /GLASS ?PANEL|FULL ?GLASS/i.test(name ?? "");
}

/**
 * Vetro satinato: nel disegno 2D/3D e' quasi opaco, il trasparente si vede attraverso.
 * Il codice di listino (VESAT) e' il riferimento; il nome dell'opzione e' il fallback
 * per le vetrature caricate a mano senza codice.
 */
export function isVetroSatinatoSelection(vetraturaValue: NullableString, vetraturaName: NullableString): boolean {
    return vetraturaValue == "VESAT" || /SATINAT/i.test(vetraturaName ?? "");
}

/**
 * Vetro trasparente: e' quello compreso nel prezzo porta, gli altri sono un supplemento
 * di listino. Per questo e' la scelta di partenza della select "Finitura vetro".
 */
export function isVetroTrasparenteSelection(vetraturaValue: NullableString): boolean {
    return vetraturaValue == "VETRA";
}

/**
 * Il GLASSPANEL in policarbonato ha un disegno suo: quattro fasce orizzontali e tre/quattro
 * campate verticali (tavola "CONFIGURAZIONE OVERLAP GLASSPANEL POLICARBONATO 6mm"),
 * mentre le versioni con vetro restano a due fasce.
 */
export function isGlasspanelPolicarbonato(modelloName: NullableString, finituraName: NullableString): boolean {
    return isGlasspanelSelection(modelloName, finituraName) && /POLICARBONATO/i.test(finituraName ?? "");
}

/**
 * Le campate verticali della vetrata glasspanel, larghezza per larghezza. A decidere quante
 * sono e' il settore, cioe' la larghezza esterno telaio: fino al settore "A" (3270mm) la
 * vetrata e' una lastra unica (tavola "CONFIGURAZIONE OVERLAP FULL GLASS - SETTORI A0-A"),
 * dal settore "C" in su entrano i due montanti e le tre lastre della tavola "...CON PROFILI
 * LATERALI - SETTORE C", dove la centrale si ferma alla larghezza massima di lastra e le due
 * laterali si spartiscono in parti uguali quello che avanza. Il policarbonato ha invece
 * campate uguali, tre fino al settore "F" (4270mm) e quattro da "G" in su.
 *
 * `larghezzaDisponibile`, `spessoreMontante` e `larghezzaMaxLastra` sono nell'unita' di misura
 * del chiamante (unita' di disegno nel 2D, metri nel 3D) e tali tornano le larghezze: e' l'unica
 * sorgente per i due disegni, che devono coincidere. Solo `larghezzaPorta` e' sempre in mm.
 */
export function getCampateGlasspanel(
    modelloName: NullableString,
    finituraName: NullableString,
    larghezzaPorta: number,
    larghezzaDisponibile: number,
    spessoreMontante: number,
    larghezzaMaxLastra: number
): number[] {
    if (!isGlasspanelSelection(modelloName, finituraName)) return [larghezzaDisponibile];

    if (isGlasspanelPolicarbonato(modelloName, finituraName)) {
        const campate = larghezzaPorta <= LARGHEZZA_MAX_SETTORE_F ? 3 : 4;
        return campateUguali(larghezzaDisponibile, spessoreMontante, campate);
    }

    if (larghezzaPorta <= LARGHEZZA_MAX_SETTORE_A) return [larghezzaDisponibile];

    const spazioLastre = larghezzaDisponibile - (2 * spessoreMontante);
    const larghezzaCentrale = Math.min(larghezzaMaxLastra, spazioLastre);
    const larghezzaLaterale = (spazioLastre - larghezzaCentrale) / 2;

    // Le tre campate restano uguali quando la lastra centrale si mangia tutto lo spazio:
    // il settore impone i montanti, ma senza margine non c'e' modo di ricavare le laterali.
    if (larghezzaLaterale <= 0) return campateUguali(larghezzaDisponibile, spessoreMontante, 3);

    return [larghezzaLaterale, larghezzaCentrale, larghezzaLaterale];
}

function campateUguali(larghezzaDisponibile: number, spessoreMontante: number, campate: number): number[] {
    const larghezzaCampata = (larghezzaDisponibile - ((campate-1) * spessoreMontante)) / campate;
    if (larghezzaCampata <= 0) return [larghezzaDisponibile];

    return Array.from({length: campate}, () => larghezzaCampata);
}

/**
 * Dove cade la sezione glasspanel dentro il pannello della PLANA: restituisce l'altezza
 * effettiva (mai oltre il pannello) e la distanza del suo bordo inferiore dal bordo inferiore
 * del pannello. Le misure sono nell'unita' del chiamante (disegno 2D, metri nel 3D): basta che
 * `altezzaPannello` e `altezzaSezione` siano nella stessa.
 */
export function getSezioneGlasspanel(
    altezzaPannello: number,
    altezzaSezione: number,
    posizione: GlasspanelPosition|undefined
): {altezza: number, dalBasso: number} {
    const altezza = Math.min(altezzaSezione, altezzaPannello);
    const spazio = altezzaPannello - altezza;

    if (posizione === "inferiore") return {altezza, dalBasso: 0};
    if (posizione === "superiore") return {altezza, dalBasso: spazio};

    return {altezza, dalBasso: spazio / 2};
}
